import SeeItInStore from "@/components/ProductDetail/SeeItInStore";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useDeliveryMessage from "@/hooks/useDeliveryMessage";
import GreenCheckIcon from "@/icons/GreenCheckIcon";
import { selectBestRoute } from "@/lib/deliveryUtils";
import { cn, isWarehouseOpen } from "@/lib/utils";
import { useGetDeliveryRouteListQuery } from "@/redux/apiSlices/deliveryApi";
import { useGetStoreDistanceQuery } from "@/redux/apiSlices/storeApi";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PickupDeliverySkeleton from "../Skeletons/PickupDeliverySkeleton";
import { Input } from "../ui/input";

const ZipCodeHandler2 = ({
  disableDelivering = false,
  onDeliveryTypeChange = () => {},
  productId = null,
  productImages,
  productName,
  productSKU,
  deliveryType = "delivery",
  seeInStore,
  status,
  soldQuantity,
  productQuantity,
  sku,
  productIsInStock,
  displayPrice,
}) => {
  const initialZipCode =
    useSelector((state) => state?.userLocation?.zipCode) || "94545";
  const latNlong = useSelector((state) => state?.userLocation?.latlng);
  const [zipCode, setZipCode] = useState("");
  const [queryZipcode, setQueryZipcode] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOption, setSelectedOption] = useState(deliveryType);

  const { data: list, isFetching } = useGetDeliveryRouteListQuery(
    {
      zipCode: queryZipcode,
      sku,
    },
    { skip: !queryZipcode || !sku }
  );

  const { isDeliverable = true } = list || {};
  const {
    route = {},
    deliveryMessage: specialDeliveryMessage,
    pickupMessage,
    pickupStatus,
  } = useMemo(() => {
    if (list?.data || !isDeliverable) {
      return selectBestRoute(
        list?.data,
        {
          status,
          soldQuantity,
          productQuantity,
          productIsInStock,
          displayPrice,
        },
        productId
      );
    }
  }, [list, isDeliverable]) || {};

  const { routeClassId, daysActive, disabledDates } = route || {};

  const { deliveryMessage: defaultDeliveryMessage, deliveryDate } =
    useDeliveryMessage({
      routeClassId,
      daysActive,
      disabledDates,
    });

  const deliveryMessage = defaultDeliveryMessage || specialDeliveryMessage;

  const { data: distance } = useGetStoreDistanceQuery(
    {
      zipCode: queryZipcode,
      // ...latNlong,
      ...(seeInStore && productId ? { productId } : {}),
    },
    { skip: !queryZipcode }
  );
  const { warehouseDistance, closestStore } = distance?.data || {};
  // const deliveryData = data?.data[0] || {};

  useEffect(() => {
    if (initialZipCode) {
      setZipCode(initialZipCode);
      setQueryZipcode(initialZipCode);
    }
  }, [initialZipCode, latNlong]);

  useEffect(() => {
    if (isDeliverable === false) {
      if (selectedOption === "delivery") {
        setSelectedOption(null);
        onDeliveryTypeChange(null);
      }
    }
  }, [isDeliverable]);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    const payload = { isPickup: option !== "delivery" }; // Determine payload dynamically
    onDeliveryTypeChange(payload);
  };

  const handleZipCodeChange = () => {
    setQueryZipcode(zipCode);
    setIsEditing(false);
  };

  const renderCheckIcon = () => (
    <GreenCheckIcon className="absolute right-[-0.4px] top-[-0.5px]" />
  );

  const formatDeliveryDate = (date) => {
    if (!date) return "";

    const deliveryDate = new Date(date);
    const month = String(deliveryDate.getMonth() + 1).padStart(2, "0");
    const day = String(deliveryDate.getDate()).padStart(2, "0");
    const year = deliveryDate.getFullYear();

    return `${month}/${day}/${year}`;
  };

  const tooltipMessages = {
    "On Order": [
      "This item is not in stock but is on order with the manufacturer.",
      "Reserve your place in line. Orders are filled in on a first-come, first-served basis.",
      "You can cancel your order at any time without any penalty.",
      "If an ETA is not listed or is listed as unknown, our sales and customer service representatives might better assist you with an accurate timeline.",
    ],
    "Special Order": [
      "Special Order products are purchased directly from the manufacturer or supplier on an order-to-order basis.",
      "Some products may be made-to-order (custom order), and some may be items that we only request stock from the manufacturer once we receive an order.",
      "If an ETA is not listed or is listed as unknown, our sales representatives might better assist you with an accurate timeline.",
    ],
  };

  const PickupTooltip = ({ message, content }) => {
    if (!content) return null;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild data-side="bottom">
            <p className="px-2 text-[14px] leading-[20px] lg:pl-3 underline underline-offset-2 cursor-pointer">
              Why is this product {message}?
            </p>
          </TooltipTrigger>
          <TooltipContent
            data-side="bottom"
            className="max-w-[300px] bg-grayBg"
          >
            <ul className="list-disc text-gray-700 text-[13px] my-2 pl-5">
              {content.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };
  const sellableQty = productQuantity - soldQuantity;

  return (
    <div>
      <div className=" gap-4 lg:gap-[14px] grid grid-cols-2 ">
        {/* Pickup */}
        <div className="relative max-w-[222px] w-full  lg:min-w-[auto] lg:max-w-full">
          <div
            onClick={() => {
              if (pickupStatus) return;
              handleOptionClick("pickup");
            }}
            className={cn(
              `cursor-pointer h-full p-5 pt-[20px] pb-[15px] border-2 rounded-10 lg:p-3 xsm:min-h-[110px]`,
              selectedOption === "pickup" ? "border-green1" : "border-gray7",
              pickupStatus && "bg-gray-100 cursor-not-allowed"
            )}
          >
            <h3 className="font-medium text-[18px] leading-[27px] mb-[6px] text-[#252525] lg:text-[14px] lg:leading-[21px] lg:mb-[1px]">
              Pickup At Warehouse
            </h3>
            {/* {isFetching && <PickupDeliverySkeleton />} */}

            {!isFetching && (
              <p className="text-[12px] leading-[18px] mb-[3px] font-medium text-gray3 lg:text-[12px] lg:leading-[18px] lg:mb-[6px]">
                {pickupStatus
                  ? pickupStatus
                  : isWarehouseOpen()
                  ? "Today"
                  : "Tomorrow"}
              </p>
            )}
            <p
              className={cn(
                "text-[14px] text-black3 font-semibold leading-[21px] lg:text-[12px] lg:leading-[18px]",
                pickupMessage === "On Order" && "text-orange1"
              )}
            >
              {pickupMessage}
            </p>
            {sellableQty > 2 && (
              <p className="text-green1 font-semibold text-sm">In-Stock</p>
            )}
          </div>
          {selectedOption === "pickup" && renderCheckIcon()}
        </div>

        {/* Delivery */}
        <div className="relative max-w-[222px] w-full  lg:min-w-[auto] lg:max-w-full">
          <div
            onClick={() => isDeliverable && handleOptionClick("delivery")}
            className={cn(
              `cursor-pointer h-full p-5 pt-[20px] pb-[15px] border-2 rounded-10 lg:p-3 xsm:min-h-[110px]`,
              selectedOption === "delivery" ? "border-green1" : "border-gray7",
              !isDeliverable && "bg-gray-100 cursor-not-allowed"
            )}
          >
            <h3 className="font-medium text-[18px] leading-[27px] mb-[6px] text-[#252525] lg:mb-[1px] lg:text-[14px] lg:leading-[21px]">
              Delivery
            </h3>
            {isFetching && <PickupDeliverySkeleton />}
            {!isFetching && (
              <>
                {["TPU60309", "TQU60307", "TQU60703", "TPU40109"].includes(sku.trim())?
                  <p className="text-[12px] leading-[18px] font-medium  ">
                    Free Shipping
                  </p>:
                  <div>
                    <p className="text-[12px] leading-[18px] font-medium mb-[3px] lg:mb-[6px] lg:text-[12px] lg:leading-[18px]">
                      {isDeliverable
                        ? deliveryMessage === "Tomorrow"
                          ? "Next Day"
                          : deliveryMessage
                        : " We only deliver to California and related Zip Codes."}
                    </p>
                    {isDeliverable && (
                      <p className="text-[12px] leading-[18px] font-medium  ">
                        {deliveryDate ? formatDeliveryDate(deliveryDate) : ""}
                      </p>
                    )}
                  </div>
                }</>
            )}
          </div>
          {selectedOption === "delivery" && renderCheckIcon()}
        </div>
      </div>
      <div className=" mb-2 items-start  gap-4  lg:gap-[14px] grid grid-cols-2 ">
        <div className="mb-2 mt-2 flex flex-col gap-4 lg:gap-[14px]">
          {productIsInStock && (
            <p className="text-[14px] leading-[20px] text-blue3 pl-5 lg:pl-3">
              Available For Pickup At Hayward Warehouse
            </p>
          )}
          {tooltipMessages[pickupMessage] && (
            <PickupTooltip
              message={pickupMessage}
              content={tooltipMessages[pickupMessage]}
            />
          )}
        </div>

        {!disableDelivering && (
          <div className="flex items-center text-[15px] leading-[23px] gap-2 flex-wrap mt-2">
            {isEditing ? (
              <>
                <Input
                  type="text"
                  value={zipCode}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,5}$/.test(value)) {
                      setZipCode(value);
                    }
                  }}
                  className="rounded-[10px] px-2 py-1 w-[100px]"
                />
                <button
                  onClick={handleZipCodeChange}
                  className="bg-gray3 text-white text-[12px]  rounded px-2 py-1"
                >
                  Change
                </button>
              </>
            ) : (
              <div className="cursor-pointer lg:text-[14px] pl-5 lg:pl-3">
                <p>
                  Estimate for{" "}
                  <span className=" text-blue5 font-medium">{zipCode}</span>{" "}
                  <span
                    onClick={() => setIsEditing(true)}
                    className="text-gray3   underline"
                  >
                    Edit
                  </span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      {/*See In Store*/}
      {seeInStore && closestStore && (
        <div className="pt-[16px] pb-[14px] pl-[38px] lg:px-[16px] lg:text-[12px] lg:leading-[18px] lg:py-[11px] w-full border-t">
          <Sheet>
            <SheetTrigger asChild>
              <p className=" text-[15px] leading-[23px]">
                See it in-store:
                <button className=" pl-1 text-blue5 font-semibold border-0 bg-transparent p-0">
                  {closestStore?.storeName} ({closestStore?.distanceMiles} miles
                  away)
                </button>
              </p>
            </SheetTrigger>
            <SheetContent className=" max-w-[500px] p-0 rounded-20">
              <SeeItInStore
                productId={productId}
                productImages={productImages}
                productName={productName}
                productSKU={productSKU}
              />
            </SheetContent>
          </Sheet>
        </div>
      )}
    </div>
  );
};

export default ZipCodeHandler2;
